import { Tooltip } from 'antd';
import { DetailField } from 'components/DetailFields';
import React from 'react';
import { criteriaForIds } from './viewModel';

const EXTERNAL_LINK_PROPS = { target: '_blank', rel: 'noopener noreferrer' };

const descriptionStyle = { maxWidth: 640 };
const paragraphStyle = { margin: '0 0 8px' };
const listStyle = { margin: '0 0 8px', paddingLeft: 20 };
const compactLinkStyle = { display: 'inline-block', fontSize: 12, marginTop: 4 };

const criteriaFrom = ({ criterion, criterionIds }) => {
  if (criterion?.id) return criteriaForIds([criterion.id]);
  return criteriaForIds(criterionIds);
};

export const WcagDescription = ({ text }) => {
  if (!text) return null;
  return (
    <div>
      {text.split('\n\n').map((block, index) => {
        const lines = block.split('\n');
        if (lines.length && lines.every(line => line.startsWith('- '))) {
          return (
            <ul key={index} style={listStyle}>
              {lines.map(line => <li key={line}>{line.slice(2)}</li>)}
            </ul>
          );
        }
        return <p key={index} style={paragraphStyle}>{block}</p>;
      })}
    </div>
  );
};

export const hasWcagCriterionHelp = ({ criterion, criterionIds } = {}) => (
  criteriaFrom({ criterion, criterionIds }).length > 0
);

export const WcagUnderstandingLink = ({ criterion, children }) => {
  if (!criterion?.understandingUrl) return null;
  return (
    <a
      href={criterion.understandingUrl}
      aria-label={`Learn more about WCAG ${criterion.id} (opens in a new tab)`}
      {...EXTERNAL_LINK_PROPS}
    >
      {children || 'Learn more'}
    </a>
  );
};

export const WcagCriterionHelp = ({ criterion, criterionIds, compact = false, showTitle = false }) => {
  const items = criteriaFrom({ criterion, criterionIds });
  if (!items.length) return null;

  if (compact) {
    return (
      <div>
        {items.map(item => (
          <Tooltip
            key={item.id}
            overlayStyle={{ maxWidth: 420 }}
            title={(
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{`${item.id} ${item.title}`}</div>
                <WcagDescription text={item.description} />
              </div>
            )}
          >
            <span style={compactLinkStyle}>
              <WcagUnderstandingLink criterion={item}>
                {items.length > 1 ? `Learn more: ${item.id}` : 'Learn more'}
              </WcagUnderstandingLink>
            </span>
          </Tooltip>
        ))}
      </div>
    );
  }

  return (
    <div style={descriptionStyle}>
      {items.map(item => (
        <div key={item.id} style={{ marginBottom: items.length > 1 ? 16 : 0 }}>
          {(showTitle || items.length > 1) && (
            <strong style={{ display: 'block', marginBottom: 6 }}>{`${item.id} ${item.title}`}</strong>
          )}
          <WcagDescription text={item.description} />
          <WcagUnderstandingLink criterion={item} />
        </div>
      ))}
    </div>
  );
};

export const WcagCriteriaDetailField = ({ criterion, criterionIds }) => {
  const ids = criterion?.id ? [criterion.id] : (criterionIds || []);
  const items = criteriaForIds(ids);
  if (!items.length) return null;
  return (
    <DetailField label={items.length > 1 ? 'Descriptions' : 'Description'}>
      <WcagCriterionHelp criterionIds={ids} showTitle={items.length > 1} />
    </DetailField>
  );
};

export default WcagCriterionHelp;
